/**
 * lib/fonts.ts — language-aware font + direction side effects.
 *
 * Responsibilities:
 *   1. Resolve the Noto Sans variant needed for the active i18n language.
 *   2. Inject a <link rel="stylesheet"> for that family into <head> exactly once.
 *   3. Toggle `document.documentElement.dir` between `ltr` / `rtl`.
 *
 * Caveat: per `.claude/skills/frontend-design/SKILL.md` the long-term target is
 * self-hosted fonts via the design-tokens package (privacy + offline PWA).
 * ROADS-146 ships the Google-Fonts variant as scaffolding — a follow-up ticket
 * (ROADS-149+) flips this to bundled WOFF2s without touching callers.
 */

const GOOGLE_FONTS_BASE = "https://fonts.googleapis.com/css2";
const LINK_ID_PREFIX = "rb-font-";

/** Per-language font family to load. `null` means the default Latin Noto Sans already covers it. */
const FONT_MAP: Record<string, string> = {
  en: "Noto Sans",
  es: "Noto Sans",
  vi: "Noto Sans",
  uk: "Noto Sans",
  zh: "Noto Sans SC",
  "zh-CN": "Noto Sans SC",
  "zh-TW": "Noto Sans TC",
  hi: "Noto Sans Devanagari",
  ar: "Noto Sans Arabic",
  ur: "Noto Sans Arabic",
  fa: "Noto Sans Arabic",
  he: "Noto Sans Hebrew",
};

const RTL_LANGS = new Set(["ar", "ur", "fa", "he"]);

/** Normalise a BCP-47 tag ("en-US") down to the primary subtag ("en"). */
function primary(lang: string): string {
  return lang.split("-")[0] ?? lang;
}

/** Load the Google Fonts stylesheet for the language's preferred Noto Sans family, if not already loaded. */
export async function ensureFontsForLanguage(lang: string): Promise<void> {
  if (typeof document === "undefined") return;
  const family = FONT_MAP[lang] ?? FONT_MAP[primary(lang)] ?? "Noto Sans";
  const id = LINK_ID_PREFIX + family.replace(/\s+/g, "-");
  if (document.getElementById(id)) return;

  const familyParam = family.replace(/\s+/g, "+");
  const href = `${GOOGLE_FONTS_BASE}?family=${familyParam}:wght@400;500;600;700&display=swap`;

  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

/** Set `<html dir>` based on the active language, plus toggle the `.rtl` helper class. */
export function applyLanguageDirection(lang: string): void {
  if (typeof document === "undefined") return;
  const p = primary(lang);
  const isRtl = RTL_LANGS.has(p);
  document.documentElement.lang = lang;
  document.documentElement.dir = isRtl ? "rtl" : "ltr";
  document.documentElement.classList.toggle("rtl", isRtl);
}
